/**
 * Custom map storage for the map editor (IndexedDB, memory fallback).
 * Map ids used in GameSettings: `custom:<id>` (singleplayer only).
 * Metadata and terrain bytes are stored separately so listing stays cheap.
 */
import type { MapMeta, MapNation } from "../../shared/protocol";
import { openStore } from "./idb";
import { randomId } from "./local";

export interface CustomMapMeta {
  id: string;
  name: string;
  description?: string;
  width: number;
  height: number;
  nations: MapNation[];
  landTiles?: number;
  createdAt: number;
  updatedAt: number;
  /** Small PNG data URL preview (optional, written by the editor). */
  thumbnail?: string;
  /** Wave 4: latitude span of the map (MapMeta.latitudes), written by the editor. */
  latitudes?: { north: number; south: number; projection?: "miller" };
  /** Wave 4 stage J (fairmap): the author keeps the deposits fixed (MapMeta.fixedDeposits). */
  fixedDeposits?: boolean;
}

interface CustomMapData {
  id: string;
  /** Terrain bytes, see src/core/map/terrain.ts */
  terrain: Uint8Array;
  /** Wave 4: biome bytes (climate / resource / river, map format v2), written by the editor. */
  biome?: Uint8Array;
}

const metaStore = openStore<CustomMapMeta>("mapMeta");
const dataStore = openStore<CustomMapData>("mapData");

export const CUSTOM_MAP_PREFIX = "custom:";

export function customMapId(id: string): string {
  return CUSTOM_MAP_PREFIX + id;
}

export function parseCustomMapId(mapId: string): string | null {
  return mapId.startsWith(CUSTOM_MAP_PREFIX) ? mapId.slice(CUSTOM_MAP_PREFIX.length) : null;
}

/** Convert stored metadata into the shared MapMeta shape used by the game. */
export function toMapMeta(meta: CustomMapMeta): MapMeta {
  return {
    id: customMapId(meta.id),
    name: meta.name,
    width: meta.width,
    height: meta.height,
    category: "fantasy",
    description: meta.description,
    nations: meta.nations,
    landTiles: meta.landTiles,
    ...(meta.latitudes ? { latitudes: meta.latitudes } : {}),
    ...(meta.fixedDeposits === true ? { fixedDeposits: true } : {}),
  };
}

export const customMaps = {
  /** All custom maps, most recently updated first. */
  async list(): Promise<CustomMapMeta[]> {
    const all = await metaStore.list();
    return all.sort((a, b) => b.updatedAt - a.updatedAt);
  },

  async getMeta(id: string): Promise<CustomMapMeta | undefined> {
    return metaStore.get(id);
  },

  /**
   * Load metadata + terrain (+ the biome layer when one was stored with the right size).
   * `id` is the bare id (without "custom:").
   */
  async load(id: string): Promise<{ meta: CustomMapMeta; terrain: Uint8Array; biome?: Uint8Array } | undefined> {
    const [meta, data] = await Promise.all([metaStore.get(id), dataStore.get(id)]);
    if (!meta || !data) return undefined;
    if (data.terrain.length !== meta.width * meta.height) return undefined;
    if (data.biome instanceof Uint8Array && data.biome.length === data.terrain.length) return { meta, terrain: data.terrain, biome: data.biome };
    return { meta, terrain: data.terrain };
  },

  /**
   * Create or update a map. Pass `meta.id` to overwrite an existing map; omit it to create
   * a new one. Returns the stored metadata.
   */
  async save(
    meta: Omit<CustomMapMeta, "id" | "createdAt" | "updatedAt"> & { id?: string; createdAt?: number },
    terrain: Uint8Array,
    biome?: Uint8Array,
  ): Promise<CustomMapMeta> {
    if (terrain.length !== meta.width * meta.height) throw new Error("terrain size does not match width*height");
    if (biome && biome.length !== terrain.length) throw new Error("biome size does not match width*height");
    const now = Date.now();
    const id = meta.id ?? randomId(12);
    const existing = meta.id ? await metaStore.get(meta.id) : undefined;
    const stored: CustomMapMeta = {
      ...meta,
      id,
      name: meta.name.trim().slice(0, 60) || "Untitled",
      createdAt: existing?.createdAt ?? meta.createdAt ?? now,
      updatedAt: now,
    };
    await dataStore.put(biome ? { id, terrain, biome } : { id, terrain });
    await metaStore.put(stored);
    return stored;
  },

  async delete(id: string): Promise<void> {
    await metaStore.delete(id);
    await dataStore.delete(id);
  },
};
